export type InsightSection = {
  heading: string;
  paragraphs: string[];
};

export type InsightCaseStudyLink = {
  studyId: string;
  title: string;
  attribution: string;
};

export type InsightRelatedPage = {
  path: string;
  label: string;
};

export type InsightArticle = {
  id: string;
  topic: string;
  title: string;
  summary: string;
  sections: InsightSection[];
  relatedCaseStudies: InsightCaseStudyLink[];
  relatedPage?: InsightRelatedPage;
};

export const insightPath = (id: string) => `/insights/${id}`;

export const insights: InsightArticle[] = [
  {
    id: 'multi-site-cms-governance',
    topic: 'Multi-site CMS governance',
    title: 'Multi-site CMS governance: what to share and what to keep separate',
    summary:
      'Organizations running several websites often face a choice between disconnected publishing tools and an overly rigid central system. Separating shared platform rules from site-level editorial workspaces lets each site serve its audience without multiplying maintenance.',
    sections: [
      {
        heading: 'The buyer problem: duplicated upkeep or bottlenecked editors',
        paragraphs: [
          'When every website, department, or program maintains its own separate content management system, routine maintenance multiplies. Security updates, template improvements, and editorial training have to be repeated across every domain, and staff moving between teams must learn different publishing tools.',
          'Moving every site into one shared system without clear boundaries creates the opposite problem. If any editor can alter shared navigation or publish to another site, teams lose confidence in the platform and route routine updates through a central technical bottleneck.',
        ],
      },
      {
        heading: 'What delivered multi-site work shows',
        paragraphs: [
          'In work delivered through our predecessor software practice, our principal designed and supported a PHP and MySQL CMS that shared one codebase across multiple branded domains. Rather than maintaining a separate publishing system for each domain, the platform separated shared infrastructure from site-level configuration.',
          'Each site kept its own branding, content, dynamic menus, and role-based editorial access while drawing on reusable templates and shared platform maintenance. Editors worked inside the sites assigned to their roles instead of navigating settings for the entire network.',
        ],
      },
      {
        heading: 'Questions to settle during discovery',
        paragraphs: [
          'As our illustrative CMS governance blueprint outlines for discovery, a multi-site platform is easier to scope when an organization settles four questions early: who can edit each content type, what can vary between site workspaces, who approves changes before publication, and who owns ongoing template and integration upkeep.',
          'Defining those boundaries before build work starts keeps central rules focused on shared templates, permissions, and maintenance while giving individual sites room to manage their own navigation and day-to-day publishing.',
        ],
      },
    ],
    relatedCaseStudies: [
      {
        studyId: 'multi-site-publishing',
        title: 'One publishing system, distinct sites',
        attribution: 'Delivered through our predecessor software practice',
      },
    ],
    relatedPage: {
      path: '/cms-governance',
      label: 'Explore the CMS governance guide',
    },
  },
  {
    id: 'policy-and-handbook-approval-workflows',
    topic: 'Policy and handbook workflows',
    title: 'Approval workflows for policy and handbook content',
    summary:
      'Official policy and handbook updates often stall when drafts, review comments, and sign-off live in email threads and shared documents. Building explicit review stages and authenticated access into the publishing tool gives editors and approvers a clear path to publication.',
    sections: [
      {
        heading: 'Why informal review breaks down on official content',
        paragraphs: [
          'Policy manuals, handbooks, and administrative updates involve multiple contributors and designated reviewers. When preparation happens in copied documents and email chains, staff cannot easily see whether a section is still a draft, awaiting review, approved, or already published.',
          'Informal handoffs also make it harder to verify who approved an update or which version is current when staff search for official guidance later.',
        ],
      },
      {
        heading: 'Patterns from earlier public-sector publishing systems',
        paragraphs: [
          'In earlier anonymous public-sector work, our principal built an administrative briefings platform on a Next.js interface and .NET API backed by SQL Server. Items moved through department, division, and executive approval levels, with administrators setting who could approve at each level, declines returned to authors with comments, email notifications at every transition, and revision history for each change.',
          'In separate earlier anonymous public-sector handbook work, our principal built a staff-only publishing application and API with single sign-on, an audit log recording each change and its reason, full-text search, and automated accessibility checks supporting ongoing quality review.',
        ],
      },
      {
        heading: 'What to define before automating review stages',
        paragraphs: [
          'A staged workflow works best when the stages match real editorial responsibility. Before building custom review queues, teams should identify which roles prepare drafts, which roles must approve before publication, what notifications are required at each transition, and how staff will search and reference published material.',
        ],
      },
    ],
    relatedCaseStudies: [
      {
        studyId: 'staged-review',
        title: 'Multi-level approval for administrative briefings',
        attribution: 'Earlier public-sector work, client anonymous',
      },
      {
        studyId: 'handbook-publishing',
        title: 'Publishing policy and handbook content',
        attribution: 'Earlier public-sector handbook work, client anonymous',
      },
    ],
  },
  {
    id: 'multilingual-cms-publishing',
    topic: 'Multilingual publishing',
    title: 'Multilingual CMS publishing across shared and local teams',
    summary:
      'Publishing in multiple languages requires more than pasting translated text into separate pages. Structured multilingual editing, clear site navigation, and scoped editorial roles help non-technical staff prepare and maintain language variants cleanly.',
    sections: [
      {
        heading: 'Where multilingual publishing adds editorial friction',
        paragraphs: [
          'When teams manage English, Spanish, French, or other language versions as unrelated pages, routine updates quickly drift out of sync. Editors have to remember which pages correspond across languages, rebuild navigation menus manually, and check formatting separately in each version.',
          'For organizations that also operate multiple sites, the complexity compounds unless language handling is built directly into the editorial interface.',
        ],
      },
      {
        heading: 'Supporting non-technical editors across languages',
        paragraphs: [
          'In multi-site CMS work delivered through our predecessor software practice, the shared PHP and MySQL publishing platform included a multilingual editor that helped non-technical staff prepare content in English, Spanish, and French alongside site-specific configuration, reusable templates, and dynamic menus.',
          'Giving editors structured places for each language variant within reusable page templates kept layout and navigation consistent while letting staff focus on accurate wording for their audience.',
        ],
      },
      {
        heading: 'Planning language structure and editorial roles',
        paragraphs: [
          'When scoping a multilingual content platform, teams benefit from deciding early which content types require language variants, how navigation and URLs should behave when a translation is prepared or updated, and which editors or reviewers are responsible for each language across shared or local sites.',
        ],
      },
    ],
    relatedCaseStudies: [
      {
        studyId: 'multi-site-publishing',
        title: 'One publishing system, distinct sites',
        attribution: 'Delivered through our predecessor software practice',
      },
    ],
    relatedPage: {
      path: '/cms-governance',
      label: 'Explore the CMS governance guide',
    },
  },
  {
    id: 'accessible-cms-authoring-workflows',
    topic: 'Accessible CMS authoring',
    title: 'Accessible CMS authoring: structure, templates, and ongoing checks',
    summary:
      'Accessible public websites depend on both the templates developers build and the content editors publish every day. Structured authoring fields, semantic page templates, and automated and manual checks help teams maintain accessibility as content changes.',
    sections: [
      {
        heading: 'Why launch-day audits drift without authoring guardrails',
        paragraphs: [
          'A website can launch with semantic landmarks, keyboard support, and readable contrast, then gradually accumulate barriers if editors must format headings, multi-column layouts, links, and tables by hand in a free-form editor.',
          'When the CMS provides structured fields and predictable templates, authors can publish updates without having to reconstruct page hierarchy or layout markup on each edit.',
        ],
      },
      {
        heading: 'Combining structured templates with automated checks',
        paragraphs: [
          'In earlier anonymous public-sector handbook work, our principal built an authenticated administrative publishing application and API that included automated accessibility checks to support ongoing quality checks as staff across departments maintained official content.',
          'In earlier custom content platform work, a database-driven builder for reusable fields, field ordering, and multi-column layouts allowed administrators to organize structured content and forms through the platform rather than improvising markup or waiting on a code change.',
        ],
      },
      {
        heading: 'Why automated checks pair with manual review',
        paragraphs: [
          'Automated checks catch common structural issues such as missing labels, heading-order gaps, and contrast failures, but they do not establish WCAG conformance or ADA compliance on their own. Meaningful link wording, logical reading order, keyboard focus behavior, zoom and reflow at enlarged text sizes, and assistive-technology usability require human review and a clear path to record, prioritize, fix, and retest issues.',
        ],
      },
    ],
    relatedCaseStudies: [
      {
        studyId: 'handbook-publishing',
        title: 'Publishing policy and handbook content',
        attribution: 'Earlier public-sector handbook work, client anonymous',
      },
      {
        studyId: 'configurable-content',
        title: 'Configurable content and forms',
        attribution: 'Earlier custom content platform work',
      },
    ],
    relatedPage: {
      path: '/about-us',
      label: 'Read our accessibility approach on About',
    },
  },
  {
    id: 'custom-application-or-cms-platform',
    topic: 'Architecture and scope decisions',
    title: 'Choosing between a custom application, a CMS, or a connected workflow',
    summary:
      'Many projects sit between a public marketing website and an internal operational system. Looking at how staff edit content, how visitors submit requests, and how often layouts change helps determine whether you need a CMS, a custom application, or both in one workflow.',
    sections: [
      {
        heading: 'When content editing and operations belong together',
        paragraphs: [
          'Organizations often start by asking for either a website redesign or a custom software tool, only to discover that staff need both in the same daily workflow. If public pages and availability or intake rules live in disconnected tools, staff spend time copying requests between systems and updating pages twice.',
          'For The Garden Marquee, Scaled Tech built a Next.js and PostgreSQL application with authenticated, role-based tools for staff to edit pages, posts, galleries, and FAQs while the visitor inquiry flow checks date availability and brings requests into an administrative workflow.',
        ],
      },
      {
        heading: 'When administrators need configurable fields and layouts',
        paragraphs: [
          'In other cases, the main bottleneck is that administrators need to organize structured content and forms without requesting a developer code change for every new layout. In earlier custom content platform work, our principal developed a database-driven builder for reusable fields, field ordering, and multi-column content layouts so administrators could configure structures directly.',
        ],
      },
      {
        heading: 'Scoping the right boundary before building',
        paragraphs: [
          'During discovery and assessment, we review who will use the system, what content or data must move between steps, and which parts of the workflow change frequently. That review clarifies whether the practical path is a focused custom application, a governed content platform, or a connected workflow that combines public publishing with operational tools.',
        ],
      },
    ],
    relatedCaseStudies: [
      {
        studyId: 'venue-workflow',
        title: 'Content and inquiries in one venue workflow',
        attribution: 'Delivered by Scaled Tech for The Garden Marquee',
      },
      {
        studyId: 'configurable-content',
        title: 'Configurable content and forms',
        attribution: 'Earlier custom content platform work',
      },
    ],
    relatedPage: {
      path: '/services',
      label: 'Explore our services and delivery process',
    },
  },
];
